import { useMatch } from "@tanstack/react-router"
import { createServerFn } from "@tanstack/react-start"
// Static import - TanStack Start handles tree-shaking for server functions
import { getRequest } from "@tanstack/react-start/server"
import Cookies from "js-cookie"
import { DEFAULT_LANGUAGE, LOCALE_COOKIE_NAME, SUPPORTED_LANGUAGES } from "./constants"

export const readLanguageFromHtmlLangAttribute = () => {
  const language = document.documentElement.lang.trim().toLowerCase()
  if (isSupportedLanguage(language)) {
    return language
  }
  return DEFAULT_LANGUAGE
}

export const getLanguageFromRequest = createServerFn({
  method: "GET",
}).handler(async () => {
  try {
    const request = getRequest()

    // Guard against getRequest() returning null/undefined during SSR
    if (!request?.headers) {
      return DEFAULT_LANGUAGE
    }

    const cookie = request.headers.get("cookie")
    const cookieLanguage = cookie
      ?.split("; ")
      .find((row: string) => row.startsWith(`${LOCALE_COOKIE_NAME}=`))
    const language = cookieLanguage?.split("=")[1]

    // Use cookie language if set by user, otherwise default to English
    // We intentionally don't use accept-language header to ensure English is the default
    if (language && isSupportedLanguage(language)) {
      return language
    }

    return DEFAULT_LANGUAGE
  } catch {
    // If getRequest() throws during SSR/hydration, fall back to default
    return DEFAULT_LANGUAGE
  }
})

export const useLanguage = () => {
  const match = useMatch({ from: "__root__" })
  const language = match.context.language
  const setLanguageFn = (locale: string) => {
    if (typeof document === "undefined") return

    const cookieOptions = {
      path: "/",
      expires: 365,
      sameSite: "lax" as const,
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    Cookies.set(LOCALE_COOKIE_NAME, locale, cookieOptions)
    window.location.reload()
  }
  return [language, setLanguageFn] as const
}

const isSupportedLanguage = (language: string | null | undefined): language is string => {
  if (!language) return false
  return SUPPORTED_LANGUAGES.includes(language.trim().toLowerCase())
}
